// Scope focus streaming: the ONE state object every streaming manager reads
// while the player is looking through an optic (monocular / sniper scope) —
// or, in VIEW mode, while the desktop / phone view is zoomed past
// view_arm.zoom_on below the band (ground colour levels only; see update()).
//
// The optic is a second camera into the same scene, but terrain, buildings and
// pins are all keyed on the PLAYER's position — so a 4 km ridge in the
// binocular would be drawn at the naked eye's detail. Focus describes a
// *wedge* (a sector from the viewer toward an anchored aim direction, wider
// than the optic's FOV) that those managers stream at close-range quality:
// the ground tree selects its nodes at the optic's pixel angle inside it and
// fills the virtual texture's optic pool from `levels` (groundtree.js,
// gmtex.js vtWant); buildings, structures and far vegetation draw on a
// dedicated layer only the optic cameras render. The naked-eye pass never
// sees a focus object.
//
// Everything here is bounded and hysteretic on purpose: the anchor only
// re-centres once the aim leaves half the margin (panning inside it must never
// refetch), the wedge lingers after the optic drops, and it is abandoned
// outright when the player leaves the apex. `gen` bumps ONLY on a real change,
// because that is what the managers use to decide to re-scan.
//
// Imports THREE, the pure lodmath and the pure hand-off only — no
// terrain/gazetteer import (that would be a cycle).
import * as THREE from 'three';
import { focusRanges } from '../../game/lodmath.js';
import { setHandoffPass } from './handoff.js';
import { deviceClass } from './device.js';

const DEG = Math.PI / 180;
const EASE_TAU = 0.2;             // s — eased anchor time constant (~200 ms)
const GEN_HALF_EPS = 0.5 * DEG;   // half-angle move that counts as a zoom change
// zoom quantum: the level ranges are re-derived when the fov crosses a step of
// this ratio (the monocular's wheel notch is x1.12 too). The ranges use the
// grid fov at or ABOVE the real one, so they are never longer than the
// formula's at the real fov — what the gates sweep.
const ZOOM_STEP = 1.12;
const LN_ZOOM_STEP = Math.log(ZOOM_STEP);

const _dir = new THREE.Vector3();
function _c1(v) { return v < -1 ? -1 : v > 1 ? 1 : v; }
// a tier block that lists `classes` is live only for those device classes
// (focus.tiers.1.trees: the desktops a GPU probe demoted to tier 1 keep the
// zoom crowns, Quest / phone at tier 1 keep the volume); null = off
export function classBlock(b, cls) {
  return b && (!Array.isArray(b.classes) || b.classes.includes(cls)) ? b : null;
}

export class Focus {
  // cfg = RENDER_CFG.focus (may be absent), tier = detailTier() (0 = off),
  // rings = RENDER_CFG.rings, the level table (a focus level's vertex spacing
  // is its tile_m / (patch_verts - 1)), cls = the device class (classBlock).
  // The per-tier budget blocks (`levels`, `buildings`, `roads`, `structures`,
  // `trees`, `shrubs`) all share one null-when-off rule: a tier the config does
  // not describe leaves them null, and every manager's focus branch keys on its
  // own block being non-null.
  constructor(cfg, tier, rings, cls = deviceClass()) {
    const c = cfg || {};
    this.layer = c.layer != null ? c.layer : 5;
    this.marginFrac = c.margin_frac != null ? c.margin_frac : 0.5;
    this.marginMinDeg = c.margin_min_deg != null ? c.margin_min_deg : 2;
    this.marginMaxDeg = c.margin_max_deg != null ? c.margin_max_deg : 4;
    this.lingerMs = c.linger_ms != null ? c.linger_ms : 4000;
    this.dropMove = c.drop_move_m != null ? c.drop_move_m : 128;
    this.loadsInFlight = c.loads_in_flight != null ? c.loads_in_flight : 6;
    this.rtPx = c.rt_px != null ? c.rt_px : 640;          // optic RT height (px)
    this.zoomBase = c.zoom_base_fov_deg != null ? c.zoom_base_fov_deg : 10;
    this.mag1x = c.detail_mag_1x > 0 ? c.detail_mag_1x : 3;  // the 1x optic over the eye
    // per-tier budgets. Tier 0 (software GL) and a config without this tier
    // mean the whole feature is off: update() is a no-op and `active` never
    // becomes true, so every manager's focus branch is dead code.
    const t = (c.tiers && c.tiers[tier]) || null;
    // {level: {range_m, max_tiles, ...}} — range_m is LIVE: derived from the
    // optic's fov (lodmath focusRanges), rewritten in place on a zoomGen bump
    this.levels = null;
    // the other managers' blocks live in _blk behind getters (below): in VIEW
    // mode every one of them reads null, so their focus branches are off
    //   buildings  {range_m, tri_budget}
    //   roads      {range_m, tri_budget} — roads3d far LOD
    //   structures {range_m, tri_budget, draw_budget} — structs2 chunks (baker 2)
    //   trees      {range_m, cap, min_px, ref_h_m[, classes]} — trees.js zoom crowns
    //   shrubs     {range_m, cap, min_px} — shrubs.js far sprites
    this._blk = { buildings: null, roads: null, structures: null, trees: null, shrubs: null };
    this._specs = [];             // lodmath focusRanges input, by level
    if (t && t.levels) {
      const byLevel = new Map((rings || []).map((r) => [r.level, r]));
      this.levels = {};
      for (const k of Object.keys(t.levels)) {
        const L = Number(k);
        const r = byLevel.get(L);
        if (!r) throw new Error(`focus: tier ${tier} level ${L} has no ring`);
        const spec = { ...t.levels[k], level: L, tile_m: r.tile_m, patch_verts: r.patch_verts, range_m: 0 };
        this.levels[L] = spec;
        this._specs.push(spec);
      }
      this._specs.sort((a, b) => a.level - b.level);
      for (const k of Object.keys(this._blk)) this._blk[k] = classBlock(t[k], cls);
    }
    // VIEW mode (ROCK-F; APP_SURFACE.md §Rev 84 "view arm"): the desktop /
    // phone MAIN camera below the optic band, zoomed past view_arm.zoom_on
    // (main.js arms it through ViewZoom.focusArm), drives the wedge too — but
    // for the terrain levels in view_arm.tiers[tier] only (the level subset
    // whose geometric want-set fits the caps at the view's wide wedge: the
    // gates sweep it). viewLevels null = this tier has no view arm.
    const va = c.view_arm || null;
    this.viewZoomOn = va && va.zoom_on > 1 ? va.zoom_on : Infinity;
    this.viewHyst = va && va.hyst > 1 ? va.hyst : 1.12;
    const vl = va && va.tiers ? va.tiers[tier] : null;
    this.viewLevels = this.levels && Array.isArray(vl)
      ? new Set(vl.map(Number).filter((L) => this.levels[L])) : null;
    if (this.viewLevels && !this.viewLevels.size) this.viewLevels = null;
    this.view = false;            // the wedge in force is the main camera's

    this.active = false;          // wedge in force (includes the linger tail)
    this.gen = 0;                 // bumps on activate / drop / re-anchor / zoom
    this.ox = 0; this.oz = 0;     // apex: player XZ at the last anchor
    this.ax = 0; this.az = -1;    // unit anchor dir XZ (north until first aim)
    this.halfRad = 0;             // hHalfFov + margin
    // the optic's zoom. vfov = the last measured optic camera's vertical fov
    // (deg), zoom = zoomBase / vfov (monocular 1..6, sniper ~1.54). zoomGen
    // bumps when the fov crosses a ZOOM_STEP grid step — the level ranges and
    // the far-vegetation sets re-derive on it; `gen` does NOT bump for zoom.
    this.vfov = this.zoomBase;
    this.zoom = 1;
    this.zoomGen = 0;
    this._zoomK = 0;              // grid step index of the ranges in force
    this.rangeFov = this.zoomBase; // the grid fov (deg) the live ranges are at
    this._applyRanges();

    // shader side: ONE Vector4 shared with terrain.js (setFocusWedge) —
    // written in place here, NEVER reassigned, or every material loses it.
    //   x,y = eased anchor dir, z = eased half-angle in RADIANS, w = pass on
    this.uWedge = { value: new THREE.Vector4(this.ax, this.az, 0, 0) };
    // ONE float uniform shared with terrain.js (setFocusWedge) and the far
    // ring: the optic zoom DURING an optic pass (setPass(true)), exactly 1
    // otherwise. Not gated on `active` — the zoom is a camera fact.
    this.uZoom = { value: 1 };

    this._axs = this.ax;          // eased anchor (chases ax/az with EASE_TAU)
    this._azs = this.az;
    this._halfGen = 0;            // halfRad as of the last gen bump
    this._margin = 0;             // rad, from the last _measure
    this._linger = 0;             // ms left of the post-optic linger
  }

  // the blocks (constructor). View mode keeps BUILDINGS (far-buildings-zoom:
  // the zoomed view admits the real GLB tiles under the same tri_budget the
  // optic band pays on this tier, and farbld.js draws the far LOD past them)
  // and TREES (zoom-crowns: the zoom crowns under the same cap); roads /
  // structures / shrubs read null there
  get buildings() { return this._blk.buildings; }
  get roads() { return this.view ? null : this._blk.roads; }
  get structures() { return this.view ? null : this._blk.structures; }
  get trees() { return this._blk.trees; }
  get shrubs() { return this.view ? null : this._blk.shrubs; }
  // the terrain levels the wedge streams now: a Set (view mode) or null (all)
  get levelsOn() { return this.view ? this.viewLevels : null; }
  // can the main camera arm the wedge on this tier at all
  get viewOk() { return !!this.viewLevels; }

  // once per frame, AFTER the optic camera is posed and BEFORE terrain.update.
  // cam = the optic camera that is actually at the eye, or null. view = cam
  // is the desktop / phone main camera below the band (main.js view arm), not
  // an optic: the level subset, no other blocks. A mode change bumps gen
  // (every manager re-scans); during the linger the last mode holds.
  update(cam, pos, dt, view = false) {
    if (cam) this._zoomFrom(cam);                   // tier or not: uZoom needs it
    if (!this.levels) return;                       // tier off
    if (cam) {
      const v = !!view && !!this.viewLevels;
      if (v !== this.view) {
        this.view = v;
        if (this.active) this.gen++;
      }
    }
    const step = Math.max(0, Math.min(0.1, dt || 0));
    // the apex is where the player STOOD — walking (or teleporting) away from
    // it makes every wedge test a lie, so the wedge goes rather than drifts.
    // With `cam` still live it re-anchors below in the same frame.
    if (this.active && Math.hypot(pos.x - this.ox, pos.z - this.oz) > this.dropMove) {
      this._drop();
    }
    if (cam) {
      const half = this._measure(cam);
      cam.getWorldDirection(_dir);
      const hl = Math.hypot(_dir.x, _dir.z);
      // straight up/down has no azimuth to anchor to — keep the last one
      const haveDir = hl > 1e-4;
      const dx = haveDir ? _dir.x / hl : this.ax;
      const dz = haveDir ? _dir.z / hl : this.az;
      if (!this.active) {
        this.ox = pos.x; this.oz = pos.z;
        this.ax = dx; this.az = dz;
        this._axs = dx; this._azs = dz;             // nothing to ease from
        this.halfRad = half;
        this._halfGen = half;
        this.active = true;
        this.gen++;
      } else {
        if (haveDir && Math.acos(_c1(dx * this.ax + dz * this.az)) > this._margin * 0.5) {
          this.ox = pos.x; this.oz = pos.z;
          this.ax = dx; this.az = dz;
          this.gen++;
        }
        // the half-angle itself is exact every frame (hit() uses it); only the
        // re-scan signal is quantised, so easing the zoom is not a load storm
        this.halfRad = half;
        if (Math.abs(half - this._halfGen) > GEN_HALF_EPS) {
          this._halfGen = half;
          this.gen++;
        }
      }
      this._linger = this.lingerMs;
    } else if (this.active) {
      // optic lowered: hold the wedge, apex and anchor frozen, so glancing
      // away and back costs nothing
      this._linger -= step * 1000;
      if (this._linger <= 0) this._drop();
    }
    this._ease(step);
  }

  // teleports (spawn / respawn / station beam) — the apex is meaningless now
  reset() { this._drop(); }

  // weapons.js calls this around the optic render: the wedge term in the
  // terrain shader may only bite during a pass that the optic cameras drive.
  // mag (ROCK-F): the desktop / phone view's magnification over its own 1x
  // eye (ViewZoom Z = fov0 / fov), for the view pass and the band pass there
  // — the ground fades then sit at Z x their 1x distance, continuous across
  // the band entry. 0 / absent (VR, the scope): zoom x detail_mag_1x as ever.
  // The tree hand-off follows the pass (handoff.js setHandoffPass): N = the
  // zoom crowns' No inside it (when trees.js committed a set), the naked N
  // outside.
  setPass(on, mag = 0) {
    this.uWedge.value.w = (on && this.active) ? 1 : 0;
    this.uZoom.value = on ? (mag > 0 ? mag / this.mag1x : this.zoom) : 1;
    setHandoffPass(on && this.active);
  }

  // does a tile's bounding disc intersect the sector? halfDiag = the disc
  // radius (tile_m * 0.707), range = this level's range_m.
  hit(cx, cz, halfDiag, range) {
    if (!this.active) return false;
    const dx = cx - this.ox, dz = cz - this.oz;
    const d = Math.hypot(dx, dz);
    if (d <= halfDiag) return true;                 // apex inside the tile
    if (d - halfDiag > range) return false;
    const ang = Math.acos(_c1((dx * this.ax + dz * this.az) / d));
    // a disc of radius halfDiag at distance d subtends asin(halfDiag/d) —
    // without that term the tiles grazing the wedge edge are dropped
    return ang <= this.halfRad + Math.asin(Math.min(1, halfDiag / d));
  }

  // |angle(tile centre - apex, anchor)| — the cache's eviction order.
  // halfDiag is optional: pass the tile's disc radius and a tile containing
  // the apex answers 0 rather than a meaningless bearing.
  angleTo(cx, cz, halfDiag = 0) {
    const dx = cx - this.ox, dz = cz - this.oz;
    const d = Math.hypot(dx, dz);
    if (d <= halfDiag || d < 1e-6) return 0;
    return Math.acos(_c1((dx * this.ax + dz * this.az) / d));
  }

  // horizontal half-FOV + margin, both derived from the optic camera itself
  // (the monocular's vertical FOV moves with the zoom every frame)
  _measure(cam) {
    const vHalf = (cam.fov || 1) * 0.5 * DEG;
    const hHalf = Math.atan((cam.aspect || 1) * Math.tan(vHalf));
    const mDeg = Math.min(this.marginMaxDeg,
      Math.max(this.marginMinDeg, this.marginFrac * (2 * hHalf) / DEG));
    this._margin = mDeg * DEG;
    return hHalf + this._margin;
  }

  _zoomFrom(cam) {
    const fov = cam.fov || this.zoomBase;
    this.vfov = fov;
    this.zoom = this.zoomBase / fov;
    // grid step at or below the real zoom (fov at or above): +1e-6 so a zoom
    // that settles exactly on a notch (1.12^n) is not read as n - 1
    const k = Math.floor(Math.log(Math.max(1e-6, this.zoom)) / LN_ZOOM_STEP + 1e-6);
    if (k !== this._zoomK) {
      this._zoomK = k;
      this.zoomGen++;
      this._applyRanges();
    }
  }

  // live level ranges at the grid fov of _zoomK, written into this.levels
  _applyRanges() {
    if (!this._specs.length) return;
    const fovQ = this.zoomBase / Math.pow(ZOOM_STEP, this._zoomK);
    this.rangeFov = fovQ;
    const r = focusRanges(this._specs, fovQ, this.rtPx);
    for (const s of this._specs) s.range_m = r[s.level];
  }

  _drop() {
    if (!this.active) return;
    this.active = false;
    this._linger = 0;
    this.uWedge.value.w = 0;
    this.gen++;
  }

  _ease(step) {
    const v = this.uWedge.value;
    if (!this.active) { v.set(this.ax, this.az, 0, 0); return; }
    const k = 1 - Math.exp(-step / EASE_TAU);
    this._axs += (this.ax - this._axs) * k;
    this._azs += (this.az - this._azs) * k;
    const l = Math.hypot(this._axs, this._azs);
    if (l > 1e-4) { this._axs /= l; this._azs /= l; }
    else { this._axs = this.ax; this._azs = this.az; }
    // NEVER claim coverage the want-set has not caught up with: while the
    // eased anchor trails the wanted one, the shader's wedge is shrunk by
    // exactly the residual error, so it can only ever be narrower than what
    // the tile loader was asked for.
    const err = Math.acos(_c1(this._axs * this.ax + this._azs * this.az));
    v.set(this._axs, this._azs, Math.max(0, this.halfRad - err), v.w);
  }
}
